import React from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    AreaChart,
    Area,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ComposedChart,
    ScatterChart,
    Scatter,
    RadialBarChart,
    RadialBar,
    Treemap,
} from "recharts";

const DEFAULT_COLORS = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff7c7c",
    "#8dd1e1",
    "#d084d0",
    "#ffb347",
    "#87ceeb",
    "#98fb98",
    "#f0e68c",
];

const isNumeric = (val) => {
    if (val === null || val === undefined) return false;
    if (typeof val === "number" && !Number.isNaN(val)) return true;
    if (typeof val === "string" && val.trim() !== "") {
        const n = Number(val);
        return !Number.isNaN(n) && Number.isFinite(n);
    }
    return false;
};

// Utility function to create histogram bins
const createHistogramBins = (data, key, bins = 10) => {
    const values = data
        .map((item) =>
            typeof item[key] === "string" ? parseFloat(item[key]) : item[key]
        )
        .filter((v) => !isNaN(v));
    if (values.length === 0) return [];

    const min = Math.min(...values);
    const max = Math.max(...values);
    const binWidth = (max - min) / bins;

    const histogramData = [];
    for (let i = 0; i < bins; i++) {
        const binStart = min + i * binWidth;
        const binEnd = min + (i + 1) * binWidth;
        const count = values.filter(
            (v) => v >= binStart && (i === bins - 1 ? v <= binEnd : v < binEnd)
        ).length;

        histogramData.push({
            bin: `${binStart.toFixed(2)}-${binEnd.toFixed(2)}`,
            count: count,
            binStart: binStart,
            binEnd: binEnd,
        });
    }

    return histogramData;
};

// Utility function to format numbers nicely
const formatNumber = (value, data, showPercentage = false) => {
    if (value === null || value === undefined || value === "") {
        return "0";
    }

    const numValue = typeof value === "string" ? parseFloat(value) : value;

    if (isNaN(numValue)) {
        return value?.toString() || "0";
    }

    // Check if all values in the dataset are below 1 (excluding 0)
    const allBelowOne =
        showPercentage &&
        data &&
        Array.isArray(data) &&
        data.length > 0 &&
        data.every((item) => {
            const val =
                item?.[
                    Object.keys(item).find(
                        (key) => key !== "label" && key !== "name"
                    )
                ];
            const num = typeof val === "string" ? parseFloat(val) : val;
            return num === 0 || (num > 0 && num < 1);
        });

    // Format the number
    let formattedValue;
    if (allBelowOne && numValue > 0) {
        // Multiply by 100 for percentage
        formattedValue = (numValue * 100).toFixed(2);
        return `${formattedValue}%`;
    } else if (showPercentage && numValue >= 1) {
        // Add % symbol for values >= 1
        formattedValue = numValue.toFixed(2);
        return `${formattedValue}%`;
    } else {
        // Regular number formatting with commas and 2 decimal places
        formattedValue = numValue.toFixed(2);
        // Add commas for thousands
        const parts = formattedValue.split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return parts.join(".");
    }
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, data, showPercentage }) => {
    if (active && payload && payload.length) {
        return (
            <div
                style={{
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "8px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    maxWidth: "300px",
                    wordBreak: "break-word",
                }}
            >
                <p
                    style={{
                        margin: "0 0 4px 0",
                        fontWeight: "bold",
                        wordBreak: "break-word",
                    }}
                >
                    {String(label).length > 50
                        ? String(label).substring(0, 50) + "..."
                        : label}
                </p>
                {payload.map((entry, index) => (
                    <p
                        key={index}
                        style={{
                            margin: "2px 0",
                            color: entry.color,
                            fontSize: "12px",
                            wordBreak: "break-word",
                        }}
                    >
                        {(entry.name || entry.dataKey || "").length > 30
                            ? (entry.name || entry.dataKey || "").substring(
                                  0,
                                  30
                              ) + "..."
                            : entry.name || entry.dataKey}
                        : {formatNumber(entry.value, data, showPercentage)}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

function ChartWrapper({
    data,
    chartType,
    xKey = "label",
    yKey = "value",
    width = "100%",
    height = 300,
    colors = DEFAULT_COLORS,
    onClick,
    vertical = false,
    showPercentage = false,
    series = null, // New prop for multi-series data
}) {
    const handleOnClick = React.useCallback(
        (data, index) => {
            if (onClick) {
                onClick(index, data);
            }
        },
        [onClick]
    );

    const { computedXKey, computedYKey, dataSeriesConfig } =
        React.useMemo(() => {
            const isMultiSeries =
                series && Array.isArray(series) && series.length > 0;

            let resolvedXKey = xKey;
            let resolvedYKey = yKey;

            if (!isMultiSeries && Array.isArray(data) && data.length > 0) {
                const firstItem =
                    data.find((d) => d && typeof d === "object") || {};
                const xVal = firstItem?.[xKey];
                const yVal = firstItem?.[yKey];
                const xIsNumeric = isNumeric(xVal);
                const yIsNumeric = isNumeric(yVal);

                const needsNumericY = [
                    "bar",
                    "line",
                    "area",
                    "pie",
                    "scatter",
                    "histogram",
                    "donut",
                    "gauge",
                    "bubble",
                    "heatmap",
                ].includes(chartType);

                if (needsNumericY && !yIsNumeric && xIsNumeric) {
                    resolvedXKey = yKey;
                    resolvedYKey = xKey;
                }
            }

            const config = isMultiSeries
                ? series.map((s, index) => ({
                      key: s.key || s.yKey || resolvedYKey,
                      name: s.name || s.key || s.yKey || resolvedYKey,
                      type: s.type || chartType,
                      color: s.color || colors[index % colors.length],
                      ...s,
                  }))
                : [
                      {
                          key: resolvedYKey,
                          name: resolvedYKey,
                          type: chartType,
                          color: colors[0],
                      },
                  ];

            return {
                computedXKey: isMultiSeries ? xKey : resolvedXKey,
                computedYKey: resolvedYKey,
                dataSeriesConfig: config,
            };
        }, [series, data, xKey, yKey, chartType, colors]);

    // Custom tick formatter for Y-axis
    const formatYAxisTick = (value) => {
        const formatted = formatNumber(value, data, showPercentage);
        // Truncate very long numbers to prevent overflow
        return formatted.length > 12
            ? formatted.substring(0, 12) + "..."
            : formatted;
    };

    // Custom tick formatter for X-axis (for numeric values)
    const formatXAxisTick = (value) => {
        if (isNumeric(value)) {
            const formatted = formatNumber(value, data, showPercentage);
            // Truncate very long numbers to prevent overflow
            return formatted.length > 12
                ? formatted.substring(0, 12) + "..."
                : formatted;
        }
        // For non-numeric values, truncate if too long
        const strValue = String(value);
        return strValue.length > 15
            ? strValue.substring(0, 15) + "..."
            : strValue;
    };

    const renderLineChart = () => (
        <LineChart
            data={data}
            margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
        >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
                dataKey={computedXKey}
                tickFormatter={formatXAxisTick}
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={60}
            />
            <YAxis
                tickFormatter={formatYAxisTick}
                tick={{ fontSize: 12 }}
                width={80}
            />
            <Tooltip
                content={
                    <CustomTooltip
                        data={data}
                        showPercentage={showPercentage}
                    />
                }
            />
            <Legend />
            {dataSeriesConfig.map((seriesItem) => (
                <Line
                    key={seriesItem.key}
                    type="monotone"
                    dataKey={seriesItem.key}
                    name={seriesItem.name}
                    stroke={seriesItem.color}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                />
            ))}
        </LineChart>
    );

    const renderBarChart = () => {
        // For horizontal bars, we need to use different configuration
        if (vertical) {
            return (
                <BarChart
                    data={data}
                    layout="vertical"
                    margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={formatYAxisTick} />
                    <YAxis
                        type="category"
                        dataKey={computedXKey}
                        width={80}
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                    />
                    <Tooltip
                        content={
                            <CustomTooltip
                                data={data}
                                showPercentage={showPercentage}
                            />
                        }
                    />
                    <Legend />
                    {dataSeriesConfig.map((seriesItem) => (
                        <Bar
                            key={seriesItem.key}
                            dataKey={seriesItem.key}
                            name={seriesItem.name}
                            fill={seriesItem.color}
                            onClick={handleOnClick}
                        />
                    ))}
                </BarChart>
            );
        }

        // Default vertical bars
        return (
            <BarChart
                data={data}
                margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey={computedXKey}
                    tickFormatter={formatXAxisTick}
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                />
                <YAxis
                    tickFormatter={formatYAxisTick}
                    tick={{ fontSize: 12 }}
                    width={80}
                />
                <Tooltip
                    content={
                        <CustomTooltip
                            data={data}
                            showPercentage={showPercentage}
                        />
                    }
                />
                <Legend />
                {dataSeriesConfig.map((seriesItem) => (
                    <Bar
                        key={seriesItem.key}
                        dataKey={seriesItem.key}
                        name={seriesItem.name}
                        fill={seriesItem.color}
                        onClick={handleOnClick}
                    />
                ))}
            </BarChart>
        );
    };

    const renderAreaChart = () => (
        <AreaChart
            data={data}
            onClick={handleOnClick}
            margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
        >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
                dataKey={computedXKey}
                tickFormatter={formatXAxisTick}
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={60}
            />
            <YAxis
                tickFormatter={formatYAxisTick}
                tick={{ fontSize: 12 }}
                width={80}
            />
            <Tooltip
                content={
                    <CustomTooltip
                        data={data}
                        showPercentage={showPercentage}
                    />
                }
            />
            <Legend />
            {dataSeriesConfig.map((seriesItem) => (
                <Area
                    key={seriesItem.key}
                    type="monotone"
                    dataKey={seriesItem.key}
                    name={seriesItem.name}
                    stroke={seriesItem.color}
                    fill={seriesItem.color}
                    fillOpacity={0.6}
                    onClick={handleOnClick}
                />
            ))}
        </AreaChart>
    );

    const renderPieChart = () => (
        <PieChart onClick={handleOnClick}>
            <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                nameKey={computedXKey}
                label={({ name, payload }) => {
                    const displayName = name ?? payload?.[computedXKey] ?? "";
                    const value = payload?.[computedYKey];
                    const formattedValue = formatNumber(
                        value,
                        data,
                        showPercentage
                    );
                    // Truncate long labels to prevent overflow
                    const truncatedName =
                        displayName.length > 20
                            ? displayName.substring(0, 20) + "..."
                            : displayName;
                    return `${truncatedName}: ${formattedValue}`;
                }}
                outerRadius={80}
                fill="#8884d8"
                dataKey={computedYKey}
                onClick={handleOnClick}
            >
                {data?.map((_, index) => (
                    <Cell
                        key={`cell-${index}`}
                        fill={colors[index % colors.length]}
                    />
                ))}
            </Pie>
            <Tooltip
                content={
                    <CustomTooltip
                        data={data}
                        showPercentage={showPercentage}
                    />
                }
            />
        </PieChart>
    );

    const renderScatterChart = () => (
        <ScatterChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
                dataKey={computedXKey}
                tickFormatter={formatXAxisTick}
                type="number"
                scale="linear"
                domain={["dataMin", "dataMax"]}
            />
            <YAxis
                tickFormatter={formatYAxisTick}
                type="number"
                scale="linear"
                domain={["dataMin", "dataMax"]}
            />
            <Tooltip
                content={
                    <CustomTooltip
                        data={data}
                        showPercentage={showPercentage}
                    />
                }
            />
            <Legend />
            {dataSeriesConfig.map((seriesItem) => (
                <Scatter
                    key={seriesItem.key}
                    dataKey={seriesItem.key}
                    name={seriesItem.name}
                    fill={seriesItem.color}
                    onClick={handleOnClick}
                />
            ))}
        </ScatterChart>
    );

    const renderHistogramChart = () => {
        const histogramData = createHistogramBins(data, computedYKey, 10);
        return (
            <BarChart data={histogramData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="bin"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                />
                <YAxis tickFormatter={formatYAxisTick} />
                <Tooltip
                    content={
                        <CustomTooltip
                            data={histogramData}
                            showPercentage={showPercentage}
                        />
                    }
                />
                <Legend />
                <Bar
                    dataKey="count"
                    name="Frequency"
                    fill={colors[0]}
                    onClick={handleOnClick}
                />
            </BarChart>
        );
    };

    const renderDonutChart = () => (
        <PieChart onClick={handleOnClick}>
            <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                nameKey={computedXKey}
                label={({ name, payload }) => {
                    const displayName = name ?? payload?.[computedXKey] ?? "";
                    const value = payload?.[computedYKey];
                    const formattedValue = formatNumber(
                        value,
                        data,
                        showPercentage
                    );
                    return `${displayName}: ${formattedValue}`;
                }}
                outerRadius={80}
                innerRadius={40}
                fill="#8884d8"
                dataKey={computedYKey}
                onClick={handleOnClick}
            >
                {data?.map((_, index) => (
                    <Cell
                        key={`cell-${index}`}
                        fill={colors[index % colors.length]}
                    />
                ))}
            </Pie>
            <Tooltip
                content={
                    <CustomTooltip
                        data={data}
                        showPercentage={showPercentage}
                    />
                }
            />
        </PieChart>
    );

    const renderGaugeChart = () => {
        const value = data && data.length > 0 ? data[0][computedYKey] : 0;
        const max = data && data.length > 0 ? data[0].max || 100 : 100;

        const gaugeData = [
            { name: "value", value: value, fill: colors[0] },
            { name: "remaining", value: max - value, fill: "#e0e0e0" },
        ];

        return (
            <RadialBarChart
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="90%"
                barSize={20}
                data={gaugeData}
                startAngle={180}
                endAngle={0}
            >
                <RadialBar dataKey="value" cornerRadius={10} fill={colors[0]} />
                <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="gauge-value"
                    fontSize="20"
                    fontWeight="bold"
                    style={{ wordBreak: "break-all", maxWidth: "80%" }}
                >
                    {formatNumber(value, data, showPercentage)}
                </text>
                <text
                    x="50%"
                    y="60%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="gauge-label"
                    fontSize="12"
                    style={{ wordBreak: "break-all", maxWidth: "80%" }}
                >
                    {data && data.length > 0
                        ? (data[0].label || "Value").length > 15
                            ? (data[0].label || "Value").substring(0, 15) +
                              "..."
                            : data[0].label || "Value"
                        : "Value"}
                </text>
            </RadialBarChart>
        );
    };

    const renderBubbleChart = () => (
        <ScatterChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
                dataKey={computedXKey}
                tickFormatter={formatXAxisTick}
                type="number"
                scale="linear"
                domain={["dataMin", "dataMax"]}
            />
            <YAxis
                tickFormatter={formatYAxisTick}
                type="number"
                scale="linear"
                domain={["dataMin", "dataMax"]}
            />
            <Tooltip
                content={
                    <CustomTooltip
                        data={data}
                        showPercentage={showPercentage}
                    />
                }
            />
            <Legend />
            {dataSeriesConfig.map((seriesItem) => (
                <Scatter
                    key={seriesItem.key}
                    dataKey={seriesItem.key}
                    name={seriesItem.name}
                    fill={seriesItem.color}
                    onClick={handleOnClick}
                />
            ))}
        </ScatterChart>
    );

    const renderHeatmapChart = () => {
        // For heatmap, we expect data in format: [{x: 'A', y: 'B', value: 10}, ...]
        // We'll create a simple heatmap using Treemap
        const heatmapData = data?.map((item, index) => ({
            name: `${item[computedXKey] || "Item"}-${
                item[computedYKey] || index
            }`,
            value: item.value || item[computedYKey] || 0,
            fill: colors[index % colors.length],
        }));

        return (
            <Treemap
                width={400}
                height={200}
                data={heatmapData}
                dataKey="value"
                ratio={4 / 3}
                stroke="#fff"
                fill="#8884d8"
            >
                <Tooltip
                    content={
                        <CustomTooltip
                            data={heatmapData}
                            showPercentage={showPercentage}
                        />
                    }
                />
            </Treemap>
        );
    };

    // New function to render mixed chart types (combination charts)
    const renderMixedChart = () => {
        return (
            <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={computedXKey} tickFormatter={formatXAxisTick} />
                <YAxis tickFormatter={formatYAxisTick} />
                <Tooltip
                    content={
                        <CustomTooltip
                            data={data}
                            showPercentage={showPercentage}
                        />
                    }
                />
                <Legend />
                {dataSeriesConfig.map((seriesItem) => {
                    const commonProps = {
                        key: seriesItem.key,
                        dataKey: seriesItem.key,
                        name: seriesItem.name,
                        stroke: seriesItem.color,
                        fill: seriesItem.color,
                    };

                    switch (seriesItem.type) {
                        case "line":
                            return (
                                <Line
                                    {...commonProps}
                                    type="monotone"
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                />
                            );
                        case "bar":
                            return (
                                <Bar {...commonProps} onClick={handleOnClick} />
                            );
                        case "area":
                            return (
                                <Area
                                    {...commonProps}
                                    type="monotone"
                                    fillOpacity={0.6}
                                    onClick={handleOnClick}
                                />
                            );
                        case "scatter":
                            return (
                                <Scatter
                                    {...commonProps}
                                    onClick={handleOnClick}
                                />
                            );
                        case "histogram":
                            return (
                                <Bar {...commonProps} onClick={handleOnClick} />
                            );
                        case "donut":
                            return (
                                <Area
                                    {...commonProps}
                                    type="monotone"
                                    fillOpacity={0.6}
                                    onClick={handleOnClick}
                                />
                            );
                        case "gauge":
                            return (
                                <Area
                                    {...commonProps}
                                    type="monotone"
                                    fillOpacity={0.6}
                                    onClick={handleOnClick}
                                />
                            );
                        case "bubble":
                            return (
                                <Scatter
                                    {...commonProps}
                                    onClick={handleOnClick}
                                />
                            );
                        case "heatmap":
                            return (
                                <Area
                                    {...commonProps}
                                    type="monotone"
                                    fillOpacity={0.6}
                                    onClick={handleOnClick}
                                />
                            );
                        default:
                            return (
                                <Line
                                    {...commonProps}
                                    type="monotone"
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                />
                            );
                    }
                })}
            </ComposedChart>
        );
    };

    const chartElement = React.useMemo(() => {
        // Check if we need to render a mixed chart (different series types)
        const hasMixedTypes =
            series &&
            series.length > 0 &&
            series.some((s) => s.type !== series[0].type);

        if (hasMixedTypes || chartType === "mixed") {
            return renderMixedChart();
        }

        switch (chartType) {
            case "line":
                return renderLineChart();
            case "bar":
                return renderBarChart();
            case "area":
                return renderAreaChart();
            case "pie":
                return renderPieChart();
            case "scatter":
                return renderScatterChart();
            case "histogram":
                return renderHistogramChart();
            case "donut":
                return renderDonutChart();
            case "gauge":
                return renderGaugeChart();
            case "bubble":
                return renderBubbleChart();
            case "heatmap":
                return renderHeatmapChart();
            default:
                return <div>Unsupported chart type: {chartType}</div>;
        }
    }, [
        chartType,
        data,
        computedXKey,
        computedYKey,
        colors,
        vertical,
        handleOnClick,
        showPercentage,
        series,
    ]);

    if (chartType === "bar" && vertical) {
        const barMinHeight = 25; // Min height for each bar to be readable
        const chartPadding = 80; // For axes, legend, etc.
        const calculatedHeight =
            (data?.length || 0) * barMinHeight + chartPadding;

        if (calculatedHeight > height) {
            return (
                <div style={{ height, overflowY: "auto" }}>
                    <ResponsiveContainer
                        width={width}
                        height={calculatedHeight}
                    >
                        {chartElement}
                    </ResponsiveContainer>
                </div>
            );
        }
    }

    // For other charts, use ResponsiveContainer
    return (
        <ResponsiveContainer width={width} height={height}>
            {chartElement}
        </ResponsiveContainer>
    );
}

const MemoizedChartWrapper = React.memo(ChartWrapper);

export { MemoizedChartWrapper as ChartWrapper };
